import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "@/components/kit";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useSession } from "@/context/SessionContext";
import { PRODUCT_MAP, fmt, dropProduct } from "@/lib/load/products";
import { allocate, partialEnds } from "@/lib/load/allocate";
import { checkSafety } from "@/lib/load/safety";
import { fullLoadGallons, grossWeight, trimToLegal } from "@/lib/load/weight";
import { useLoadData } from "@/lib/load/useLoadData";
import { STARTER_TRAILER } from "@/lib/load/store";
import { TrailerBar, CustomerBar } from "@/components/load/Pickers";
import { OrderCard } from "@/components/load/OrderCard";
import { TankerSvg } from "@/components/load/TankerSvg";
import { PocketGrid } from "@/components/load/PocketGrid";
import { SafetyAlerts } from "@/components/load/SafetyAlerts";
import { RackCard } from "@/components/load/RackCard";
import { TrailerModal } from "@/components/load/TrailerModal";
import { CustomerModal, NEW_CUSTOMER } from "@/components/load/CustomerModal";

const START_ROWS = [{ key: "r0", product: "gas87" }];
const GROUP_KEY = "fhp-load-group";

export default function LoadPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const { patch } = useSession();
  const nav = useNavigate();
  const [msg, setMsg] = useState(null);
  const say = useCallback((text, tone) => setMsg({ text, tone }), []);
  const d = useLoadData(user, say);
  const [amounts, setAmounts] = useState({});
  const [rows, setRows] = useState(START_ROWS);
  const [allocated, setAllocated] = useState(false);
  const [modal, setModal] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [custModal, setCustModal] = useState(null);
  const [group, setGroupState] = useState(localStorage.getItem(GROUP_KEY) !== "0");
  const setGroup = (g) => { localStorage.setItem(GROUP_KEY, g ? "1" : "0"); setGroupState(g); };

  const trailer = d.trailer;
  const comps = useMemo(() => trailer?.compartments || [], [trailer]);
  const customer = d.customers.find((c) => c.id === customerId) || null;
  const lines = useMemo(() => (customer
    ? customer.tanks.map((x, i) => ({ key: `t${i}`, product: PRODUCT_MAP[x.product] ? x.product : "gas87", label: x.name, gallons: amounts[`t${i}`] || 0 }))
    : rows.map((r) => ({ key: r.key, product: r.product, label: null, gallons: amounts[r.key] || 0 }))), [customer, amounts, rows]);
  const opts = { group, dieselDouble: !!trailer?.diesel_in_double };
  const result = useMemo(() => (allocated && trailer ? allocate(comps, lines, { group, dieselDouble: !!trailer.diesel_in_double }) : null), [allocated, comps, lines, group, trailer]);
  const warnings = useMemo(() => (result ? checkSafety(result.loads, comps) : []), [result, comps]);
  const halfEnds = result ? partialEnds(comps, result.loads) : [];
  const activeShell = comps.filter((c) => !c.locked_dry).reduce((a, c) => a + c.capacity, 0);

  const setAmount = (key, v) => setAmounts((o) => ({ ...o, [key]: Math.max(0, Math.min(99999, v)) }));
  const clear = () => { setAmounts({}); setRows(START_ROWS); setAllocated(false); setMsg(null); };
  const addRow = () => setRows((rs) => [...rs, { key: `r${crypto.randomUUID().slice(0, 8)}`, product: rs[rs.length - 1]?.product === "ulsd" ? "gas87" : "ulsd" }]);
  const pickCustomer = (id) => { setCustomerId(id); setAmounts({}); setAllocated(false); };

  const overBy = trailer ? Math.round(grossWeight(trailer, lines) - trailer.max_gross) : 0;
  const runAllocate = () => {
    if (!lines.some((l) => l.gallons > 0)) return say("Enter at least one ordered product first", "danger");
    if (overBy > 0) {
      const fix = trimToLegal(trailer, lines);
      setAmounts((o) => ({ ...o, ...Object.fromEntries(fix.lines.map((l) => [l.key, l.gallons])) }));
      setAllocated(true);
      const what = fix.removed.map((x) => `${fmt(x.gallons)} gal ${x.label || PRODUCT_MAP[x.product].name.toUpperCase()}`).join(", ");
      setTimeout(() => document.querySelector('[data-testid="load-message"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
      return say(t("OVERWEIGHT BY {x} LB. Took off {w} to get under {m} lb gross.", { x: fmt(overBy), w: what, m: fmt(trailer.max_gross) }), "danger");
    }
    const r = allocate(comps, lines, opts);
    setAllocated(true);
    if (r.shortfalls.length) say("The order is bigger than the open pockets. See what won't fit.", "caution");
    else say(t("Loaded {g} gal into {n} pockets", { g: fmt(r.loads.reduce((a, l) => a + l.gallons, 0)), n: r.loads.filter((l) => l.product).length }), "safe");
  };
  const fullLoad = (key) => {
    const g = fullLoadGallons(key, lines, trailer, activeShell, opts);
    const next = lines.map((l) => (l.key === key ? { ...l, gallons: g } : l));
    setAmount(key, g);
    setAllocated(g > 0);
    if (g === 0) say("No legal weight or open space left for this product", "danger");
    else say(t("Full load: {g} gal · est. gross {w} lb", { g: fmt(g), w: fmt(Math.round(grossWeight(trailer, next))) }), "safe");
  };
  const planDrop = (load) => {
    const product = dropProduct(load.product);
    patch({ dropGallons: load.gallons, ...(product ? { product } : {}) });
    nav("/drop");
  };

  if (!d.trailers) return <Screen testID="load-screen" title={t("Load the truck")}><div className="card-sub" data-testid="load-loading">…</div></Screen>;
  return (
    <Screen testID="load-screen" title={t("Load the truck")} subtitle={t("Split your order into the trailer's pockets")}>
      {!user && <div className="card-sub load-note" data-testid="load-local-note">{t("Not signed in: trailers and customers stay on this phone. Sign in to keep them on any phone.")}</div>}
      {trailer ? <TrailerBar trailers={d.trailers} activeId={trailer.id} onSelect={d.setActiveId} onEdit={() => setModal("edit")} onNew={() => setModal("new")} t={t} />
        : <button type="button" className="primary-btn" onClick={() => setModal("new")} data-testid="first-trailer-btn">{t("Set up your trailer")}</button>}
      {trailer && (<>
        <OrderCard lines={lines} setAmount={setAmount} t={t} onAdd={customer ? null : addRow}
          onProduct={customer ? null : (key, product) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, product } : r)))}
          onRemove={customer ? null : (key) => { setRows((rs) => rs.filter((r) => r.key !== key)); setAmounts(({ [key]: _, ...rest }) => rest); }}
          activeShell={activeShell} onClear={clear} onAllocate={runAllocate} shortfalls={result?.shortfalls} group={group} setGroup={setGroup} trailer={trailer} onFull={fullLoad}
          customerBar={<CustomerBar customers={d.customers} customerId={customer?.id} onSelect={pickCustomer} onEdit={() => setCustModal("edit")} onNew={() => setCustModal("new")} t={t} />} />
        {msg && (msg.tone === "danger"
          ? <div className="drop-warn drop-warn-danger" data-testid="load-message" role="alert"><b className="drop-warn-head">{t(msg.text)}</b></div>
          : <div className={`scan-message tone-${msg.tone}`} data-testid="load-message">{t(msg.text)}</div>)}
        {overBy > 0 && <div className="drop-warn drop-warn-danger"  data-testid="overweight-warning" role="alert"><b className="drop-warn-head">{t("OVERWEIGHT BY {x} LB", { x: fmt(overBy) })}</b></div>}
        <div className="card load-card" data-testid="pockets-card">
          <div className="section-title"><span className="load-step">2</span>{t("Pockets")}</div>
          {trailer.diesel_in_double && <div className="card-sub" data-testid="diesel-db-rule">{t("Diesel goes in double-bulkhead (DB) pockets only.")}</div>}
          <div className="card-sub">{t("Tap Lock dry to keep a pocket empty. Tap Plan this drop to send a pocket to Plan a drop.")}</div>
          <div className="load-svg-wrap"><TankerSvg compartments={comps} loads={result?.loads} warnings={warnings} /></div>
          <SafetyAlerts warnings={warnings} t={t} />
          {halfEnds.map((i) => (
            <div key={i} className="scan-message tone-caution" data-testid={`partial-end-warning-${i}`}>
              {t("{c} ({w}) is only partly full: {g} of {cap} gal. A half-full front or back pocket shifts weight and sloshes. Change the order so it's full or empty, or lock it dry.",
                { c: comps[i].name, w: t(i === 0 ? "front" : "rear"), g: fmt(result.loads[i].gallons), cap: fmt(comps[i].capacity) })}
            </div>
          ))}
          <PocketGrid compartments={comps} loads={result?.loads} onToggle={d.toggleDry} onPlan={planDrop} t={t} />
        </div>
        <RackCard trailer={trailer} customer={customer} loads={result?.loads} warnings={warnings} t={t} />
      </>)}
      {modal && (
        <TrailerModal key={modal + (trailer?.id || "")} t={t} initial={modal === "new" || !trailer ? STARTER_TRAILER : trailer} isNew={modal === "new" || !trailer}
          canDelete={d.trailers.length > 1} onClose={() => setModal(null)}
          onSave={async (data) => { if (await d.saveTrailer(data, modal === "new" || !trailer)) setModal(null); }}
          onDelete={async () => { if (window.confirm(t("Delete this trailer?")) && await d.removeTrailer()) setModal(null); }} />
      )}
      {custModal && (
        <CustomerModal key={custModal + (customer?.id || "")} t={t} initial={custModal === "new" ? NEW_CUSTOMER : customer} isNew={custModal === "new"} onClose={() => setCustModal(null)}
          onSave={async (data) => { const c = await d.saveCustomer(data, custModal === "new" ? null : customer.id); if (c) { pickCustomer(c.id); setCustModal(null); } }}
          onDelete={async () => { if (window.confirm(t("Delete this customer?")) && await d.removeCustomer(customer.id)) { pickCustomer(null); setCustModal(null); } }} />
      )}
    </Screen>
  );
}
